/* Bespoke three.js objects layered onto react-globe.gl's `customLayerData`.

   react-globe.gl gives us three accessors:
     - customThreeObject(d)        → build the Object3D for datum d
     - customThreeObjectUpdate(o,d)→ position/orient it each render
   plus getCoords()/getGlobeRadius() on the instance for spherical placement.

   We model two datum kinds here:
     - "column"  → an incident risk pillar rising radially from its lat/lng
     - "ripple"  → an expanding ring that fades, fired on new high-sev incidents

   The atmosphere shell is a separate one-off mesh (buildAtmosphere) added once
   to the scene by GlobeCanvas — it has no per-datum data.

   A single module-level `CLOCK` is the shared time source so every shader
   (atmosphere breathing, column scan-bands, ripple expansion) is frame-synced.
   `reducedMotion` zeroes the animated terms without removing the visuals.
*/
import * as THREE from "three"
import {
  ATMOSPHERE_FRAG,
  ATMOSPHERE_VERT,
  COLUMN_FRAG,
  COLUMN_VERT,
  RIPPLE_FRAG,
  RIPPLE_VERT,
} from "./shaders"
import { GLOBE_PALETTE, hexToRgb01, severityColumnColor } from "../globe-colors"
import type { GlobePoint } from "../types"

const timer = new THREE.Timer()
let sharedTime = 0
let reducedMotion = false

/** Advance the shared clock; call once per animation frame from GlobeCanvas. */
export function tickClock(): number {
  timer.update()
  // Under reduced-motion we still advance a touch so the breathing settles, but
  // GlobeCanvas freezes the per-frame tick instead — see its onGlobeReady loop.
  sharedTime += timer.getDelta()
  return sharedTime
}

export function setReducedMotion(v: boolean) {
  reducedMotion = v
}

export function currentTime(): number {
  return sharedTime
}

// --- Datum model ----------------------------------------------------------

export type CustomDatum = ColumnDatum | RippleDatum

export interface ColumnDatum {
  type: "column"
  id: string
  lat: number
  lng: number
  /** column height as a fraction of globe radius */
  height: number
  /** base radius of the pillar in globe-radius units */
  radius: number
  color: string
  seed: number
  label: string
  severity: string
}

export interface RippleDatum {
  type: "ripple"
  id: string
  lat: number
  lng: number
  /** disc radius in globe-radius units at full expansion */
  size: number
  color: string
  /** ms epoch when the ripple was spawned */
  bornMs: number
  /** total lifetime in ms */
  lifeMs: number
}

// --- Severity → geometry helpers -----------------------------------------

function severityHeight(severity: string | undefined): number {
  switch (severity) {
    case "critical":
      return 0.42
    case "high":
    case "warn":
      return 0.28
    case "medium":
      return 0.18
    default:
      return 0.1
  }
}

function severityRadius(severity: string | undefined): number {
  switch (severity) {
    case "critical":
      return 0.018
    case "high":
    case "warn":
      return 0.014
    default:
      return 0.011
  }
}

/** Is this incident severe enough to warrant a one-shot ripple on appearance? */
export function isHighSeverity(p: GlobePoint): boolean {
  return p.severity === "critical" || p.severity === "high"
}

/** Build the column datum array from the incident points. */
export function buildColumnData(incidents: GlobePoint[]): ColumnDatum[] {
  return incidents.map((p, i) => ({
    type: "column" as const,
    id: p.id,
    lat: p.lat,
    lng: p.lng,
    height: severityHeight(p.severity),
    radius: severityRadius(p.severity),
    color: p.owned ? GLOBE_PALETTE.fleet : severityColumnColor(p.severity),
    seed: (hashId(p.id) % 1000) / 1000 + i * 0.013,
    label: p.label,
    severity: p.severity ?? "info",
  }))
}

function hashId(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

// --- Geometry singletons (shared across instances) ------------------------
// Unit cylinder: radius 1, height 1, base at y=0 (translated up by 0.5).
const COLUMN_GEO = (() => {
  const g = new THREE.CylinderGeometry(1, 1, 1, 18, 1, true)
  g.translate(0, 0.5, 0)
  return g
})()

const RIPPLE_GEO = new THREE.PlaneGeometry(2, 2, 1, 1)

// --- Object factory -------------------------------------------------------

export function makeCustomObject(d: object): THREE.Object3D {
  const datum = d as CustomDatum
  if (datum.type === "column") return makeColumn(datum)
  return makeRipple(datum)
}

function makeColumn(d: ColumnDatum): THREE.Object3D {
  const [r, g, b] = hexToRgb01(d.color)
  const mat = new THREE.ShaderMaterial({
    vertexShader: COLUMN_VERT,
    fragmentShader: COLUMN_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    uniforms: {
      uColor: { value: new THREE.Color(r, g, b) },
      uTime: { value: 0 },
      uSeed: { value: d.seed },
      uIntensity: { value: 1.0 },
    },
  })
  const mesh = new THREE.Mesh(COLUMN_GEO, mat)
  mesh.renderOrder = 5
  mesh.userData.kind = "column"
  return mesh
}

function makeRipple(d: RippleDatum): THREE.Object3D {
  const [r, g, b] = hexToRgb01(d.color)
  const mat = new THREE.ShaderMaterial({
    vertexShader: RIPPLE_VERT,
    fragmentShader: RIPPLE_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    uniforms: {
      uColor: { value: new THREE.Color(r, g, b) },
      uProgress: { value: 0 },
    },
  })
  const mesh = new THREE.Mesh(RIPPLE_GEO, mat)
  mesh.renderOrder = 6
  mesh.userData.kind = "ripple"
  return mesh
}

// --- Per-frame update (called by react-globe.gl) --------------------------
// `globe` is the imperative instance, used for getCoords/getGlobeRadius.

interface GlobeLike {
  getCoords(lat: number, lng: number, altitude?: number): {
    x: number
    y: number
    z: number
  }
  getGlobeRadius(): number
}

export function updateCustomObject(
  obj: THREE.Object3D,
  d: object,
  globe: GlobeLike,
): void {
  const datum = d as CustomDatum
  if (datum.type === "column") updateColumn(obj as THREE.Mesh, datum, globe)
  else updateRipple(obj as THREE.Mesh, datum, globe)
}

const UP = new THREE.Vector3(0, 1, 0)
const FWD = new THREE.Vector3(0, 0, 1)
const _surface = new THREE.Vector3()
const _outward = new THREE.Vector3()
const _quat = new THREE.Quaternion()

function updateColumn(mesh: THREE.Mesh, d: ColumnDatum, globe: GlobeLike): void {
  const R = globe.getGlobeRadius()
  const c = globe.getCoords(d.lat, d.lng, 0)
  _surface.set(c.x, c.y, c.z)
  // Outward radial direction = surface position normalised (globe is origin).
  _outward.copy(_surface).normalize()
  mesh.position.copy(_surface)
  // Orient the cylinder's +Y axis along the outward radial.
  _quat.setFromUnitVectors(UP, _outward)
  mesh.quaternion.copy(_quat)
  // Scale: radius in world units, height as fraction of R.
  const radiusWorld = d.radius * R
  const heightWorld = d.height * R
  mesh.scale.set(radiusWorld, heightWorld, radiusWorld)

  const mat = mesh.material as THREE.ShaderMaterial
  mat.uniforms.uTime!.value = reducedMotion ? 0 : sharedTime
  mat.uniforms.uIntensity!.value = reducedMotion ? 0.75 : 1.0
}

function updateRipple(mesh: THREE.Mesh, d: RippleDatum, globe: GlobeLike): void {
  const R = globe.getGlobeRadius()
  // Lift the disc just above the surface so it sits flush, facing outward.
  const c = globe.getCoords(d.lat, d.lng, 0.002)
  _surface.set(c.x, c.y, c.z)
  _outward.copy(_surface).normalize()
  mesh.position.copy(_surface)
  // Plane's +Z normal should point outward.
  _quat.setFromUnitVectors(FWD, _outward)
  mesh.quaternion.copy(_quat)
  const sizeWorld = d.size * R
  mesh.scale.set(sizeWorld, sizeWorld, 1)

  const mat = mesh.material as THREE.ShaderMaterial
  const elapsed = Date.now() - d.bornMs
  const progress = Math.min(1, elapsed / d.lifeMs)
  mat.uniforms.uProgress!.value = reducedMotion ? Math.min(progress, 0.5) : progress
}

// --- Atmosphere shell (one-off, added directly to the scene) --------------

export interface AtmosphereHandle {
  mesh: THREE.Mesh
  update(time: number): void
  dispose(): void
}

export function buildAtmosphere(globeRadius: number): AtmosphereHandle {
  const [r, g, b] = hexToRgb01(GLOBE_PALETTE.accent)
  const [dr, dg, db] = hexToRgb01(GLOBE_PALETTE.atmosphereDeep)
  const mat = new THREE.ShaderMaterial({
    vertexShader: ATMOSPHERE_VERT,
    fragmentShader: ATMOSPHERE_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.BackSide,
    uniforms: {
      uColor: { value: new THREE.Color(r, g, b) },
      uColorDeep: { value: new THREE.Color(dr, dg, db) },
      uIntensity: { value: 1.15 },
      uPower: { value: 3.2 },
      uTime: { value: 0 },
    },
  })
  // Shell sits ~15% larger than the globe so the rim sits just off the limb.
  const geo = new THREE.SphereGeometry(globeRadius * 1.15, 64, 64)
  const mesh = new THREE.Mesh(geo, mat)
  mesh.renderOrder = -1
  return {
    mesh,
    update(time: number) {
      mat.uniforms.uTime!.value = reducedMotion ? 0 : time
    },
    dispose() {
      geo.dispose()
      mat.dispose()
    },
  }
}

// --- Custom-layer tooltip / click hit-testing -----------------------------

export function customLayerLabel(d: object): string {
  const datum = d as CustomDatum
  if (datum.type !== "column") return ""
  const esc = (s: string) =>
    s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c] ?? c)
  const sev = datum.severity
    ? ` <span style="color:${datum.color};text-transform:uppercase;letter-spacing:0.1em">${esc(datum.severity)}</span>`
    : ""
  return `<div style="font:600 11px ui-monospace,SF Mono,monospace;background:rgba(15,20,25,0.95);border:1px solid #2a2a2a;border-radius:3px;padding:5px 9px;color:#e8e8e8;white-space:nowrap">${esc(datum.label)}${sev}</div>`
}
